
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "position-openings"
    }}>{`Position openings`}</h1>
    <p>{`A `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionOpening"
      }}><inlineCode parentName="a">{`PositionOpening`}</inlineCode></a>{` represents an open position at a hirer’s organization.
For example, a position opening might be created once a hirer approves a job requisition for a new role.`}</p>
    <p>{`A newly created `}<inlineCode parentName="p">{`PositionOpening`}</inlineCode>{` consists of an owner and a status code.
The details of the position itself (e.g. its title & location) are contained in `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode>{`s`}</a>{` created within the opening:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`A `}<a parentName="p" {...{
            "href": "/schema/named-type/PostedPositionProfile"
          }}><inlineCode parentName="a">{`PostedPositionProfile`}</inlineCode></a>{` represents a `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad"
          }}>{`job ad`}</a>{` posted to the SEEK job board.`}</p>
        <p parentName="li">{`Hirers might continue to post job ads within the same opening while the position remains unfilled.
They could also use multiple job ads to cover different `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/locations"
          }}>{`locations`}</a>{` and `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/job-categories"
          }}>{`job categories`}</a>{`.`}</p>
        <p parentName="li">{`Grouping multiple job ads within a single opening is provided for your convenience.
There is no business impact from grouping (or not grouping) job ads together.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`An `}<a parentName="p" {...{
            "href": "/schema/named-type/UnpostedPositionProfile"
          }}><inlineCode parentName="a">{`UnpostedPositionProfile`}</inlineCode></a>{` contains information about a `}<a parentName="p" {...{
            "href": "/use-cases/proactive-sourcing/unposted-positions"
          }}>{`position that hasn’t been posted to SEEK`}</a>{`.`}</p>
        <p parentName="li">{`Your software would share these with SEEK to support `}<a parentName="p" {...{
            "href": "/use-cases/proactive-sourcing"
          }}>{`Talent Search Connect`}</a>{`.
Grouping unposted positions with their related job ads helps SEEK match potential candidates against the opening.`}</p>
      </li>
    </ul>
    <p>{`While all job ads need to belong to a position opening,
your software can create a new position opening for each job ad.
The `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad#postposition"
      }}><inlineCode parentName="a">{`postPosition`}</inlineCode>{` mutation`}</a>{` is provided to create a new position opening and post a job ad in a single operation.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://talent.seek.com.au"
      }}>{`SEEK employer website`}</a>{` and retired Job Posting API do not have an analogous concept of an open position.
Job ads posted through those channels will have a synthetic `}<inlineCode parentName="p">{`PositionOpening`}</inlineCode>{` created for each of their `}<inlineCode parentName="p">{`PositionProfile`}</inlineCode>{`s.
Synthetic position openings cannot be `}<a parentName="p" {...{
        "href": "#positionopenings"
      }}>{`paginated`}</a>{`, updated or deleted.`}</p>
    <h2 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h2>
    <p>{`Before you start with position opening queries, you will need to `}<a parentName="p" {...{
        "href": "/auth/partner-tokens"
      }}>{`request a partner token`}</a>{`.`}</p>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <p>{`The following sample mutations are provided for demonstration purposes only;
they are `}<a parentName="p" {...{
        "href": "/graphql/playground"
      }}>{`not executable in the Playground environment`}</a>{`.
You should use your live partner credentials to post a job ad under your `}<a parentName="p" {...{
        "href": "/introduction#integration-process"
      }}>{`unsearchable SEEK test hirer`}</a>{` for end-to-end testing.`}</p>
    <h3 {...{
      "id": "createpositionopening"
    }}>{`createPositionOpening`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/createPositionOpening"
      }}><inlineCode parentName="a">{`createPositionOpening`}</inlineCode>{` mutation`}</a>{` creates an empty position opening.
The `}<a parentName="p" {...{
        "href": "/schema/named-type/PostingRequester"
      }}><inlineCode parentName="a">{`postingRequester`}</inlineCode></a>{` indicates the SEEK hirer and the `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/contact-people"
      }}>{`contact details`}</a>{` of the opening’s owner.`}</p>
    <pre><code parentName="pre" {...{
        "8517": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: CreatePositionOpeningInput!) {\\n  createPositionOpening(input: $input) {\\n    positionOpening {\\n      documentId {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":45,\"column\":1,\"offset\":2391},\"end\":{\"line\":55,\"column\":4,\"offset\":2570},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"positionOpening\\\": {\\n      \\\"postingRequester\\\": {\\n        \\\"roleCode\\\": \\\"Company\\\",\\n        \\\"id\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n        \\\"personContacts\\\": [\\n          {\\n            \\\"name\\\": { \\\"formattedName\\\": \\\"Jeff Manager\\\" },\\n            \\\"roleCode\\\": \\\"HiringManager\\\",\\n            \\\"communication\\\": {\\n              \\\"email\\\": [{ \\\"address\\\": \\\"jeff.manager@example.com\\\" }],\\n              \\\"phone\\\": [{ \\\"formattedNumber\\\": \\\"03 8517 4100\\\" }]\\n            }\\n          }\\n        ]\\n      },\\n      \\\"seekPartnerMetadata\\\": \\\"position-requisition-123\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":57,\"column\":1,\"offset\":2572},\"end\":{\"line\":79,\"column\":4,\"offset\":3157},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"createPositionOpening\\\": {\\n    \\\"positionOpening\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":81,\"column\":1,\"offset\":3159},\"end\":{\"line\":91,\"column\":4,\"offset\":3353},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "CreatePositionOpeningInput!)": true,
        "{\\n": true,
        "": true,
        "createPositionOpening(input:": true,
        "$input)": true,
        "positionOpening": true,
        "documentId": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":45,\"column\":1,\"offset\":2391},\"end\":{\"line\":55,\"column\":4,\"offset\":2570},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"positionOpening\\\":": true,
        "\\\"postingRequester\\\":": true,
        "\\\"roleCode\\\":": true,
        "\\\"Company\\\",\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"personContacts\\\":": true,
        "[\\n": true,
        "\\\"name\\\":": true,
        "{": true,
        "\\\"formattedName\\\":": true,
        "\\\"Jeff": true,
        "Manager\\\"": true,
        "},\\n": true,
        "\\\"HiringManager\\\",\\n": true,
        "\\\"communication\\\":": true,
        "\\\"email\\\":": true,
        "[{": true,
        "\\\"address\\\":": true,
        "\\\"jeff.manager@example.com\\\"": true,
        "}],\\n": true,
        "\\\"phone\\\":": true,
        "\\\"formattedNumber\\\":": true,
        "\\\"03": true,
        "4100\\\"": true,
        "}]\\n": true,
        "]\\n": true,
        "\\\"seekPartnerMetadata\\\":": true,
        "\\\"position-requisition-123\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":57,\"column\":1,\"offset\":2572},\"end\":{\"line\":79,\"column\":4,\"offset\":3157},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"createPositionOpening\\\":": true,
        "\\\"documentId\\\":": true,
        "\\\"value\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":81,\"column\":1,\"offset\":3159},\"end\":{\"line\":91,\"column\":4,\"offset\":3353},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: CreatePositionOpeningInput!) {
  createPositionOpening(input: $input) {
    positionOpening {
      documentId {
        value
      }
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "updatepositionopeningstatus"
    }}>{`updatePositionOpeningStatus`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/updatePositionOpeningStatus"
      }}><inlineCode parentName="a">{`updatePositionOpeningStatus`}</inlineCode>{` mutation`}</a>{` updates the status of the position opening.`}</p>
    <p>{`A position opening’s status is intended to help hirers manage their position openings;
it isn’t used directly by the SEEK API.
For example, you can filter on a particular status when `}<a parentName="p" {...{
        "href": "#positionopenings"
      }}>{`paginating position openings`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: UpdatePositionOpeningStatusInput!) {\\n  updatePositionOpeningStatus(input: $input) {\\n    positionOpening {\\n      statusCode\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":101,\"column\":1,\"offset\":3713},\"end\":{\"line\":109,\"column\":4,\"offset\":3880},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"positionOpening\\\": {\\n      \\\"documentId\\\": \\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\",\\n      \\\"statusCode\\\": \\\"Closed\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":111,\"column\":1,\"offset\":3882},\"end\":{\"line\":120,\"column\":4,\"offset\":4063},\"indent\":[1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"updatePositionOpeningStatus\\\": {\\n    \\\"positionOpening\\\": {\\n      \\\"statusCode\\\": \\\"Closed\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":122,\"column\":1,\"offset\":4065},\"end\":{\"line\":130,\"column\":4,\"offset\":4179},\"indent\":[1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "UpdatePositionOpeningStatusInput!)": true,
        "{\\n": true,
        "": true,
        "updatePositionOpeningStatus(input:": true,
        "$input)": true,
        "positionOpening": true,
        "statusCode\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":101,\"column\":1,\"offset\":3713},\"end\":{\"line\":109,\"column\":4,\"offset\":3880},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"positionOpening\\\":": true,
        "\\\"documentId\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\",\\n": true,
        "\\\"statusCode\\\":": true,
        "\\\"Closed\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":111,\"column\":1,\"offset\":3882},\"end\":{\"line\":120,\"column\":4,\"offset\":4063},\"indent\":[1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"updatePositionOpeningStatus\\\":": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":122,\"column\":1,\"offset\":4065},\"end\":{\"line\":130,\"column\":4,\"offset\":4179},\"indent\":[1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: UpdatePositionOpeningStatusInput!) {
  updatePositionOpeningStatus(input: $input) {
    positionOpening {
      statusCode
    }
  }
}
`}</code></pre>
    <p>{`After an opening has been created you can use separate mutations to create nested `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode>{`s`}</a>{`.
For example, the `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad#postpositionprofileforopening"
      }}><inlineCode parentName="a">{`postPositionProfileForOpening`}</inlineCode>{` mutation`}</a>{` will create a job ad within the opening.`}</p>
    <h3 {...{
      "id": "updatepositionopeningpersoncontacts"
    }}>{`updatePositionOpeningPersonContacts`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/updatePositionOpeningPersonContacts"
      }}><inlineCode parentName="a">{`updatePositionOpeningPersonContacts`}</inlineCode>{` mutation`}</a>{` updates the `}<inlineCode parentName="p">{`postingRequester`}</inlineCode>{` `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/contact-people"
      }}>{`contact details`}</a>{` of the position opening.`}</p>
    <pre><code parentName="pre" {...{
        "2695": true,
        "6523": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: UpdatePositionOpeningPersonContactsInput!) {\\n  updatePositionOpeningPersonContacts(input: $input) {\\n    positionOpening {\\n      documentId {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":139,\"column\":1,\"offset\":4556},\"end\":{\"line\":149,\"column\":4,\"offset\":4763},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"positionOpening\\\": {\\n      \\\"documentId\\\": \\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\",\\n      \\\"personContacts\\\": [\\n        {\\n          \\\"name\\\": {\\n            \\\"given\\\": \\\"Gabriella\\\",\\n            \\\"family\\\": \\\"Hettinger\\\",\\n            \\\"formattedName\\\": \\\"Gabriella Hettinger\\\"\\n          },\\n          \\\"communication\\\": {\\n            \\\"phone\\\": [{ \\\"formattedNumber\\\": \\\"09 2695 0948\\\" }],\\n            \\\"email\\\": [{ \\\"address\\\": \\\"g.hettinger@example.com\\\" }]\\n          },\\n          \\\"roleCode\\\": \\\"HiringManager\\\"\\n        },\\n        {\\n          \\\"name\\\": {\\n            \\\"given\\\": \\\"Matthew\\\",\\n            \\\"family\\\": \\\"Roberts\\\",\\n            \\\"formattedName\\\": \\\"Matthew Roberts\\\"\\n          },\\n          \\\"communication\\\": {\\n            \\\"phone\\\": [{ \\\"formattedNumber\\\": \\\"09 6523 3714\\\" }],\\n            \\\"email\\\": [{ \\\"address\\\": \\\"matty_r32@example.com\\\" }]\\n          },\\n          \\\"roleCode\\\": \\\"Recruiter\\\"\\n        }\\n      ]\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":151,\"column\":1,\"offset\":4765},\"end\":{\"line\":185,\"column\":4,\"offset\":5696},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"updatePositionOpeningPersonContacts\\\": {\\n    \\\"positionOpening\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":187,\"column\":1,\"offset\":5698},\"end\":{\"line\":197,\"column\":4,\"offset\":5906},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "UpdatePositionOpeningPersonContactsInput!)": true,
        "{\\n": true,
        "": true,
        "updatePositionOpeningPersonContacts(input:": true,
        "$input)": true,
        "positionOpening": true,
        "documentId": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":139,\"column\":1,\"offset\":4556},\"end\":{\"line\":149,\"column\":4,\"offset\":4763},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"positionOpening\\\":": true,
        "\\\"documentId\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\",\\n": true,
        "\\\"personContacts\\\":": true,
        "[\\n": true,
        "\\\"name\\\":": true,
        "\\\"given\\\":": true,
        "\\\"Gabriella\\\",\\n": true,
        "\\\"family\\\":": true,
        "\\\"Hettinger\\\",\\n": true,
        "\\\"formattedName\\\":": true,
        "\\\"Gabriella": true,
        "Hettinger\\\"\\n": true,
        "},\\n": true,
        "\\\"communication\\\":": true,
        "\\\"phone\\\":": true,
        "[{": true,
        "\\\"formattedNumber\\\":": true,
        "\\\"09": true,
        "0948\\\"": true,
        "}],\\n": true,
        "\\\"email\\\":": true,
        "\\\"address\\\":": true,
        "\\\"g.hettinger@example.com\\\"": true,
        "}]\\n": true,
        "\\\"roleCode\\\":": true,
        "\\\"HiringManager\\\"\\n": true,
        "\\\"Matthew\\\",\\n": true,
        "\\\"Roberts\\\",\\n": true,
        "\\\"Matthew": true,
        "Roberts\\\"\\n": true,
        "3714\\\"": true,
        "\\\"matty_r32@example.com\\\"": true,
        "\\\"Recruiter\\\"\\n": true,
        "]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":151,\"column\":1,\"offset\":4765},\"end\":{\"line\":185,\"column\":4,\"offset\":5696},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"updatePositionOpeningPersonContacts\\\":": true,
        "\\\"value\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":187,\"column\":1,\"offset\":5698},\"end\":{\"line\":197,\"column\":4,\"offset\":5906},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: UpdatePositionOpeningPersonContactsInput!) {
  updatePositionOpeningPersonContacts(input: $input) {
    positionOpening {
      documentId {
        value
      }
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "positionopening"
    }}>{`positionOpening`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/query/positionOpening"
      }}><inlineCode parentName="a">{`positionOpening`}</inlineCode>{` query`}</a>{` returns information about an existing position opening.
You can select fields from its nested `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionOpening#/field/paginatedPositionProfiles"
      }}><inlineCode parentName="a">{`PositionOpening.paginatedPositionProfiles`}</inlineCode></a>{` to retrieve information about a subset of its job ads.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  positionOpening(id: $id) {\\n    documentId {\\n      value\\n    }\\n    statusCode\\n    paginatedPositionProfiles {\\n      edges {\\n        node {\\n          profileId {\\n            value\\n          }\\n          positionTitle\\n        }\\n      }\\n      pageInfo {\\n        hasNextPage\\n        endCursor\\n      }\\n    }\\n    postingRequester {\\n      id {\\n        value\\n      }\\n    }\\n    seekPartnerMetadata\\n  }\\n}\",\"position\":{\"start\":{\"line\":204,\"column\":1,\"offset\":6155},\"end\":{\"line\":233,\"column\":4,\"offset\":6587},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"globalPublicTest:positionOpening:positions:F65aT5rAv3ZSusuunFWT8i\\\"\\n}\",\"position\":{\"start\":{\"line\":235,\"column\":1,\"offset\":6589},\"end\":{\"line\":239,\"column\":4,\"offset\":6680},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"positionOpening\\\": {\\n    \\\"documentId\\\": {\\n      \\\"value\\\": \\\"globalPublicTest:positionOpening:positions:F65aT5rAv3ZSusuunFWT8i\\\"\\n    },\\n    \\\"statusCode\\\": \\\"Active\\\",\\n    \\\"paginatedPositionProfiles\\\": {\\n      \\\"edges\\\": [\\n        {\\n          \\\"node\\\": {\\n            \\\"profileId\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:27cuZeA47\\\"\\n            },\\n            \\\"positionTitle\\\": \\\"Customer Mobility Director\\\"\\n          }\\n        }\\n      ],\\n      \\\"pageInfo\\\": {\\n        \\\"hasNextPage\\\": false,\\n        \\\"endCursor\\\": \\\"globalPublicTest:positionProfileForOpeningCursor:positions:2gffoubCoNbaVMEAKupmSyz7WF6SwX35G5G6qdMRK3yxmHbsjppVMdB48W273ZELzjSaiaLtMHpjeEPyBKdzot38C3qY4wpdwDa\\\"\\n      }\\n    },\\n    \\\"postingRequester\\\": {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n      }\\n    },\\n    \\\"seekPartnerMetadata\\\": \\\"{\\\\\\\"id\\\\\\\":456,\\\\\\\"title\\\\\\\":\\\\\\\"Customer Mobility Director\\\\\\\"}\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":241,\"column\":1,\"offset\":6682},\"end\":{\"line\":272,\"column\":4,\"offset\":7599},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "positionOpening(id:": true,
        "$id)": true,
        "documentId": true,
        "value\\n": true,
        "}\\n": true,
        "statusCode\\n": true,
        "paginatedPositionProfiles": true,
        "edges": true,
        "node": true,
        "profileId": true,
        "positionTitle\\n": true,
        "pageInfo": true,
        "hasNextPage\\n": true,
        "endCursor\\n": true,
        "postingRequester": true,
        "id": true,
        "seekPartnerMetadata\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":204,\"column\":1,\"offset\":6155},\"end\":{\"line\":233,\"column\":4,\"offset\":6587},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:F65aT5rAv3ZSusuunFWT8i\\\"\\n}\",\"position\":{\"start\":{\"line\":235,\"column\":1,\"offset\":6589},\"end\":{\"line\":239,\"column\":4,\"offset\":6680},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"positionOpening\\\":": true,
        "\\\"documentId\\\":": true,
        "\\\"value\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:F65aT5rAv3ZSusuunFWT8i\\\"\\n": true,
        "},\\n": true,
        "\\\"statusCode\\\":": true,
        "\\\"Active\\\",\\n": true,
        "\\\"paginatedPositionProfiles\\\":": true,
        "\\\"edges\\\":": true,
        "[\\n": true,
        "\\\"node\\\":": true,
        "\\\"profileId\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:27cuZeA47\\\"\\n": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Customer": true,
        "Mobility": true,
        "Director\\\"\\n": true,
        "],\\n": true,
        "\\\"pageInfo\\\":": true,
        "\\\"hasNextPage\\\":": true,
        "false,\\n": true,
        "\\\"endCursor\\\":": true,
        "\\\"globalPublicTest:positionProfileForOpeningCursor:positions:2gffoubCoNbaVMEAKupmSyz7WF6SwX35G5G6qdMRK3yxmHbsjppVMdB48W273ZELzjSaiaLtMHpjeEPyBKdzot38C3qY4wpdwDa\\\"\\n": true,
        "\\\"postingRequester\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n": true,
        "\\\"seekPartnerMetadata\\\":": true,
        "\\\"{\\\\\\\"id\\\\\\\":456,\\\\\\\"title\\\\\\\":\\\\\\\"Customer": true,
        "Director\\\\\\\"}\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":241,\"column\":1,\"offset\":6682},\"end\":{\"line\":272,\"column\":4,\"offset\":7599},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  positionOpening(id: $id) {
    documentId {
      value
    }
    statusCode
    paginatedPositionProfiles {
      edges {
        node {
          profileId {
            value
          }
          positionTitle
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
    postingRequester {
      id {
        value
      }
    }
    seekPartnerMetadata
  }
}
`}</code></pre>
    <h3 {...{
      "id": "positionopenings"
    }}>{`positionOpenings`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/query/positionOpenings"
      }}><inlineCode parentName="a">{`positionOpenings`}</inlineCode>{` query`}</a>{` returns a paginated list of position openings for a given hirer.
Only position openings created by the SEEK API will appear in the paginated list.`}</p>
    <p>{`You can optionally use a `}<inlineCode parentName="p">{`statusCode`}</inlineCode>{` filter to only return position openings with the desired status.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($hirerId: String!, $statusCode: String) {\\n  positionOpenings(hirerId: $hirerId, filter: { statusCode: $statusCode }) {\\n    edges {\\n      node {\\n        paginatedPositionProfiles {\\n          edges {\\n            node {\\n              profileId {\\n                value\\n              }\\n            }\\n          }\\n          pageInfo {\\n            hasNextPage\\n            endCursor\\n          }\\n        }\\n      }\\n    }\\n    pageInfo {\\n      hasNextPage\\n      endCursor\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":281,\"column\":1,\"offset\":7906},\"end\":{\"line\":307,\"column\":4,\"offset\":8398},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n  \\\"statusCode\\\": \\\"Active\\\"\\n}\",\"position\":{\"start\":{\"line\":309,\"column\":1,\"offset\":8400},\"end\":{\"line\":314,\"column\":4,\"offset\":8501},\"indent\":[1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($hirerId:": true,
        "String!,": true,
        "$statusCode:": true,
        "String)": true,
        "{\\n": true,
        "": true,
        "positionOpenings(hirerId:": true,
        "$hirerId,": true,
        "filter:": true,
        "{": true,
        "statusCode:": true,
        "$statusCode": true,
        "})": true,
        "edges": true,
        "node": true,
        "paginatedPositionProfiles": true,
        "profileId": true,
        "value\\n": true,
        "}\\n": true,
        "pageInfo": true,
        "hasNextPage\\n": true,
        "endCursor\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":281,\"column\":1,\"offset\":7906},\"end\":{\"line\":307,\"column\":4,\"offset\":8398},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"statusCode\\\":": true,
        "\\\"Active\\\"\\n}\",\"position\":{\"start\":{\"line\":309,\"column\":1,\"offset\":8400},\"end\":{\"line\":314,\"column\":4,\"offset\":8501},\"indent\":[1,1,1,1,1]}}]": true
      }}>{`query ($hirerId: String!, $statusCode: String) {
  positionOpenings(hirerId: $hirerId, filter: { statusCode: $statusCode }) {
    edges {
      node {
        paginatedPositionProfiles {
          edges {
            node {
              profileId {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "deletepositionopening"
    }}>{`deletePositionOpening`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/deletePositionOpening"
      }}><inlineCode parentName="a">{`deletePositionOpening`}</inlineCode>{` mutation`}</a>{` deletes an empty position opening.`}</p>
    <p>{`Because every position profile must be nested inside an opening, you must delete all nested position profiles first:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Unposted position profiles can be deleted using the `}<a parentName="p" {...{
            "href": "/schema/mutation/deleteUnpostedPositionProfile"
          }}><inlineCode parentName="a">{`deleteUnpostedPositionProfile`}</inlineCode>{` mutation`}</a>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The position profiles of job ads cannot be explicitly deleted; they will be automatically deleted 180 days after the job ad closes.`}</p>
      </li>
    </ul>
    <p>{`To ensure data consistency, deletion of a position opening is prevented for 48 hours after it has been:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Created through the `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad#postposition"
          }}><inlineCode parentName="a">{`postPosition`}</inlineCode>{` mutation`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Referenced in a `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad#postpositionprofileforopening"
          }}><inlineCode parentName="a">{`postPositionProfileForOpening`}</inlineCode>{` mutation`}</a></p>
      </li>
    </ul>
    <p>{`To soft delete a position opening you can instead `}<a parentName="p" {...{
        "href": "#updatepositionopeningstatus"
      }}>{`update its status`}</a>{` to `}<inlineCode parentName="p">{`Closed`}</inlineCode>{`.
You can then filter on `}<inlineCode parentName="p">{`Active`}</inlineCode>{` position openings when using the `}<a parentName="p" {...{
        "href": "/schema/query/positionOpenings"
      }}><inlineCode parentName="a">{`positionOpenings`}</inlineCode>{` query`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: DeletePositionOpeningInput!) {\\n  deletePositionOpening(input: $input) {\\n    positionOpening {\\n      documentId {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":334,\"column\":1,\"offset\":9379},\"end\":{\"line\":344,\"column\":4,\"offset\":9558},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"positionOpening\\\": {\\n      \\\"documentId\\\": \\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":346,\"column\":1,\"offset\":9560},\"end\":{\"line\":354,\"column\":4,\"offset\":9711},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"deletePositionOpening\\\": {\\n    \\\"positionOpening\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":356,\"column\":1,\"offset\":9713},\"end\":{\"line\":366,\"column\":4,\"offset\":9907},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "DeletePositionOpeningInput!)": true,
        "{\\n": true,
        "": true,
        "deletePositionOpening(input:": true,
        "$input)": true,
        "positionOpening": true,
        "documentId": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":334,\"column\":1,\"offset\":9379},\"end\":{\"line\":344,\"column\":4,\"offset\":9558},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"positionOpening\\\":": true,
        "\\\"documentId\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":346,\"column\":1,\"offset\":9560},\"end\":{\"line\":354,\"column\":4,\"offset\":9711},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"deletePositionOpening\\\":": true,
        "\\\"value\\\":": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":356,\"column\":1,\"offset\":9713},\"end\":{\"line\":366,\"column\":4,\"offset\":9907},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: DeletePositionOpeningInput!) {
  deletePositionOpening(input: $input) {
    positionOpening {
      documentId {
        value
      }
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;